import { useState } from 'react';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import type { UserMenuSection } from '@leagueplatform/solutions-foundation';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

// Settings menu composition per the settings-profile-ootb change (spec:
// settings-navigation → Menu Composition Reference, PRD REQ-502 order;
// labels per the R1 lo-fi desktop menu, Figma 3364:179928).
// MS-1 ships the shell only: rows without a wired destination render
// visible-but-inert; destinations land with settings-profile-ootb-2.
// App-only rows (biometric unlock, notification preferences, app rating,
// app version — REQ-502/509/64) are omitted on web; Connected devices stays
// on web (REQ-502). Eligibility gating for Pharmacy set up and Premium
// payment (REQ-502) ships with their SSO wiring; until then they render
// inert like every other unwired row.
const INERT_ROW = () => {};

export const useUserSettingsLinksConfig = () => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const [modal, setModal] = useState<'deactivate' | 'deactivated' | null>(null);
  const userMenu: UserMenuSection[] = [
    {
      ...(isMobile && { heading: formatMessage({ id: 'STR_ID_CARD' }) }),
      items: [
        {
          label: formatMessage({
            id: isMobile ? 'STR_VIEW_ID_CARD' : 'STR_ID_CARD',
          }),
          to: getModulePath(LEAGUE_MODULE_NAMES.benefits),
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_ACCOUNT_SETUP_AND_SECURITY' }),
      items: [
        {
          label: formatMessage({ id: 'STR_SHARED_FAMILY_ACCESS' }),
          onClick: INERT_ROW,
        },
        {
          label: formatMessage({ id: 'STR_MANAGE_CAREGIVERS' }),
          to: APP_MANAGED_ROUTES.CAREGIVER,
        },
        {
          label: formatMessage({ id: 'STR_SECURITY_SETTINGS' }),
          onClick: INERT_ROW,
        },
        {
          label: formatMessage({ id: 'STR_CONNECTED_DEVICES' }),
          onClick: INERT_ROW,
        },
        {
          label: formatMessage({ id: 'STR_DEACTIVATE_ACCOUNT' }),
          onClick: () => setModal('deactivate'),
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_PROFILE' }),
      items: [
        {
          label: formatMessage({ id: 'STR_PERSONAL_INFORMATION' }),
          to: APP_MANAGED_ROUTES.PERSONAL_INFORMATION,
        },
        {
          label: formatMessage({ id: 'STR_DEPENDENT_INFORMATION' }),
          onClick: INERT_ROW,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_COMMUNICATION_AND_NOTIFICATIONS' }),
      items: [
        {
          label: formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' }),
          to: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_PHARMACY_AND_PRESCRIPTIONS' }),
      items: [
        {
          label: formatMessage({ id: 'STR_PHARMACY_SETTINGS' }),
          onClick: INERT_ROW,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_DOCUMENTS_AND_FORMS' }),
      items: [
        {
          label: formatMessage({ id: 'STR_DOCUMENTS_HUB' }),
          onClick: INERT_ROW,
        },
        {
          label: formatMessage({ id: 'STR_CLAIMS_REIMBURSEMENT' }),
          onClick: INERT_ROW,
        },
        {
          label: formatMessage({ id: 'STR_COORDINATION_OF_BENEFITS' }),
          onClick: INERT_ROW,
        },
        {
          label: formatMessage({ id: 'STR_PREMIUM_PAYMENT' }),
          onClick: INERT_ROW,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_HELP' }),
      items: [
        {
          label: formatMessage({ id: 'STR_SUPPORT' }),
          to: APP_MANAGED_ROUTES.SUPPORT,
        },
        {
          label: formatMessage({ id: 'STR_MESSAGING' }),
          to: `${getModulePath(LEAGUE_MODULE_NAMES.messaging)}/threads`,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_FEEDBACK' }),
      items: [
        {
          label: formatMessage({ id: 'STR_SHARE_FEEDBACK' }),
          onClick: INERT_ROW,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_DISCLAIMERS' }),
      items: [
        {
          label: formatMessage({ id: 'STR_NEED_NAME_OF_DISCLAIMER_LINKS' }),
          onClick: INERT_ROW,
        },
      ],
    },
    {
      items: [
        {
          label: formatMessage({ id: 'STR_SIGN_OUT' }),
          to: APP_MANAGED_ROUTES.SIGN_OUT,
          variant: 'critical',
        },
      ],
    },
  ];

  return { userMenu, modal, setModal };
};
